// src/pages/Layout.jsx
import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NavbarMobile from "../components/NavbarMobile";
import OfflineOverlay from "../components/OfflineOverlay";
import CookieConsent from "../components/CookieConsent";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ Cookie banner styles
import "../pages/styles/cookieConsent.css";

const shouldHideHeader = (pathname) => {
  const noHeaderExact = [
    "/signup",
    "/login",
    "/forgot-password",
    "/identification",
    "/verification-code",
    "/profile-details",
  ];

  if (pathname.startsWith("/reset-password")) return true;
  return noHeaderExact.includes(pathname);
};

const shouldHideFooter = (pathname) => {
  const noFooterExact = [
    "/checkout",
    "/login",
    "/signup",
    "/forgot-password",
    "/identification",
    "/verification-code",
    "/profile-details",
  ];

  if (pathname.startsWith("/reset-password")) return true;
  return noFooterExact.includes(pathname);
};

const shouldHideNavbarMobile = (pathname) => {
  const productPathPattern = /^\/product\/[^/]+$/;

  const noNavbarExact = [
    "/signup",
    "/login",
    "/forgot-password",
    "/identification",
    "/verification-code",
    "/profile-details",
    "/checkout",
  ];

  if (pathname.startsWith("/reset-password")) return true;

  return productPathPattern.test(pathname) || noNavbarExact.includes(pathname);
};

const Layout = ({ isOffline, onRetry, isLoading }) => {
  const location = useLocation();

  const hideHeader = shouldHideHeader(location.pathname);
  const hideFooter = shouldHideFooter(location.pathname);
  const hideNavbarMobile = shouldHideNavbarMobile(location.pathname);

  // ✅ compute real header + navbar space and push overlay into the middle
  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.documentElement;

    const getH = (sel) => {
      const el = document.querySelector(sel);
      if (!el) return 0;

      // if hidden via display:none, height will be 0
      const r = el.getBoundingClientRect?.();
      return r?.height ? Math.round(r.height) : 0;
    };

    const computeOffsets = () => {
      // ---------------------------
      // TOP OFFSET (header area)
      // ---------------------------
      let top = 0;

      // mobile headers (your CSS shows these on <=820px)
      const appHomeH = getH(".appHomeH");
      const appH = getH(".appH");

      // desktop fixed header parts
      const rbTopbar = getH(".rb-topbar");
      const rbMainbar = getH(".rb-mainbar");

      // ✅ prefer whichever is actually visible
      if (appHomeH || appH) {
        top = appHomeH || appH; // only one should be visible at a time
      } else {
        top = rbTopbar + rbMainbar;
      }

      // if your header is hidden on auth pages etc
      if (hideHeader) top = 0;

      // ---------------------------
      // BOTTOM OFFSET (mobile navbar)
      // ---------------------------
      let bottom = 0;

      // your bottom nav wrapper class is .jmNav-wrap (fixed)
      const jmNavH = getH(".jmNav-wrap");
      if (!hideNavbarMobile && jmNavH) {
        bottom = jmNavH + (window?.CSS?.supports?.("padding-bottom: env(safe-area-inset-bottom)") ? 0 : 0);
      } else {
        bottom = 0;
      }

      root.style.setProperty("--offlineTop", `${top}px`);
      root.style.setProperty("--offlineBottom", `${bottom}px`);
    };

    computeOffsets();

    // re-measure when layout changes (scroll can change stuck header position)
    window.addEventListener("resize", computeOffsets);
    window.addEventListener("scroll", computeOffsets, { passive: true });

    // also re-measure shortly after route changes/render
    const t = setTimeout(computeOffsets, 50);

    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", computeOffsets);
      window.removeEventListener("scroll", computeOffsets);
    };
  }, [hideHeader, hideNavbarMobile, location.pathname]);

  return (
    <>
      {!hideHeader && <Header />}

      <main style={{ position: "relative", minHeight: "100vh" }}>
        <Outlet />

        {/* ✅ OfflineOverlay now sits exactly between header and navbar */}
        {isOffline && <OfflineOverlay onRetry={onRetry} />}
      </main>

      {!hideFooter && <Footer />}
      {!hideNavbarMobile && <NavbarMobile />}

      {/* ✅ Cookie consent banner (global) */}
      <CookieConsent
        policyPath="/privacy-policy"
        cookieNoticePath="/cookie-notice"
        position="bottom-left"
      />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default Layout;
